"use client";

import { useState, useRef } from "react";
import NameCardGrid from "./name-card-grid";
import { useSwipePage } from "@/lib/use-swipe-page";
import { useGridColumns } from "./use-grid-columns";
import { useDoubleTap } from "@/lib/use-double-tap";

// ── Layout constant ───────────────────────────────────────────────────────────
// CARD_WIDTH is the rendered width of each name card in pixels (including
// gap); column count is computed dynamically from container width via
// useGridColumns (see use-grid-columns.ts).
// CARD_WIDTH ≈ 120px  GAP ≈ 6px  (Tailwind gap-1.5 = 6px)
export const CARD_WIDTH = 120;
export const CARD_GAP   = 6;

// ── Types ─────────────────────────────────────────────────────────────────────

interface MatchHistoryEntry {
  matchNumber: number;
  round: number;
  won: boolean;
  tied: boolean;
  opponent: string;
  isTieBreaker: boolean;
  isRematch: boolean;
  winnerStreak: number;
  myVotes: number;
  opponentVotes: number;
  commentary: string | null;
}

interface NameData {
  id: string;
  name: string;
  meaning: string | null;
  origin: string | null;
  notes: string | null;
  winCount: number;
  lossCount: number;
  tieCount: number;
  winStreak: number;
  eliminated: boolean;
  resurrected: boolean;
  lcKVoteCount: number;
  status: string;
  matchHistory: MatchHistoryEntry[];
  lcKEligible?: boolean;
  lckOpen?: boolean;
}

const PAGE_SIZE = 9; // 3×3 grid — update when dynamic columns land

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildLetterRanges(sorted: NameData[]): string[] {
  const totalPages = Math.ceil(sorted.length / PAGE_SIZE);
  return Array.from({ length: totalPages }, (_, p) => {
    const slice = sorted.slice(p * PAGE_SIZE, p * PAGE_SIZE + PAGE_SIZE);
    const first = slice[0]?.name[0]?.toUpperCase() ?? "";
    const last  = slice[slice.length - 1]?.name[0]?.toUpperCase() ?? "";
    return first === last ? first : `${first}–${last}`;
  });
}

// Returns the first (leftmost) letter of a label, e.g. "A–C" → "A", "B" → "B"
function firstLetter(label: string): string {
  return label[0]?.toUpperCase() ?? "";
}

// Returns true when a label represents a single letter ("A"), false for ranges ("A–C")
function isSingleLetter(label: string): boolean {
  return label.length === 1;
}

/**
 * Pre-compute double-tap jump targets for every page.
 *
 * Forward jump from page P:
 *   - If current label is a single letter (e.g. "A"): jump to the first page
 *     whose label starts with a *different* letter — i.e. the start of the next letter.
 *   - If current label is a range (e.g. "A–C"): jump 3 pages forward.
 *   Either way, capped at totalPages - 1.
 *
 * Backward jump from page P:
 *   - If current label is a single letter: find the first page whose label starts
 *     with a letter *earlier* than current (scanning backward), then rewind to the
 *     very first page that shares that same starting letter — i.e. the start of the
 *     previous letter's run.
 *   - If current label is a range: jump 3 pages backward.
 *   Either way, clamped to 0.
 */
function buildJumpTargets(
  letterRanges: string[],
  totalPages: number,
): { jumpFwd: number[]; jumpBck: number[] } {
  const jumpFwd  = new Array<number>(totalPages);
  const jumpBck  = new Array<number>(totalPages);

  for (let p = 0; p < totalPages; p++) {
    const label = letterRanges[p] ?? "";

    // ── Forward ──
    if (isSingleLetter(label)) {
      // Find the first page after P that starts with a different letter
      let target = totalPages - 1;
      for (let q = p + 1; q < totalPages; q++) {
        if (firstLetter(letterRanges[q] ?? "") !== firstLetter(label)) {
          target = q;
          break;
        }
      }
      jumpFwd[p] = target;
    } else {
      jumpFwd[p] = Math.min(p + 3, totalPages - 1);
    }

    // ── Backward ──
    if (isSingleLetter(label)) {
      // Find the closest page before P that starts with a different (earlier) letter
      let prevLetter = "";
      for (let q = p - 1; q >= 0; q--) {
        const fl = firstLetter(letterRanges[q] ?? "");
        if (fl !== firstLetter(label)) {
          prevLetter = fl;
          break;
        }
      }
      if (!prevLetter) {
        // Already on the first letter — jump to page 0
        jumpBck[p] = 0;
      } else {
        // Rewind to the *first* page that shares prevLetter
        let start = 0;
        for (let q = 0; q < p; q++) {
          if (firstLetter(letterRanges[q] ?? "") === prevLetter) {
            start = q;
            break;
          }
        }
        jumpBck[p] = start;
      }
    } else {
      jumpBck[p] = Math.max(p - 3, 0);
    }
  }

  return { jumpFwd, jumpBck };
}

// ── Double-tap nav button ─────────────────────────────────────────────────────

function NavButton({
  label, disabled, onTap, onDoubleTap, align,
}: {
  label: string; disabled: boolean; onTap: () => void; onDoubleTap: () => void; align: "left" | "right";
}) {
  const handleTap = useDoubleTap(onTap, onDoubleTap);
  return (
    <button
      onClick={() => { if (!disabled) handleTap(); }}
      className={`shrink-0 px-3 py-1.5 rounded-xl text-xs font-black transition select-none ${align === "left" ? "text-left" : "text-right"}`}
      style={{ WebkitUserSelect: "none", userSelect: "none", color: "#7c3aed", opacity: disabled ? 0.25 : 1, pointerEvents: disabled ? "none" : "auto" }}
      aria-label={label}
    >
      {label}
    </button>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function NameCardPager({
  names,
  lckRequired,
  userVotedIds,
  futuresOddsMap,
  userBalance,
  myFuturesBets,
  highlightId,
}: {
  names: NameData[];
  lckRequired: number;
  userVotedIds?: Set<string>;
  // Optional: passed from roster page to enable in-card futures betting.
  futuresOddsMap?: Record<string, number>;
  userBalance?: number;
  myFuturesBets?: Record<string, { wager: number; odds: number }>;
  // Optional: a nameId to jump straight to (and auto-open) on mount — the
  // deep-link target from global search.
  highlightId?: string;
}) {
  const sorted = [...names].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
  );

  const totalPages   = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const letterRanges = buildLetterRanges(sorted);
  const { jumpFwd, jumpBck } = buildJumpTargets(letterRanges, totalPages);

  const highlightIndex = highlightId ? sorted.findIndex((n) => n.id === highlightId) : -1;
  const initialPage = highlightIndex >= 0 ? Math.floor(highlightIndex / PAGE_SIZE) : 0;
  const [page, setPage] = useState(initialPage);

  // Dynamic column count — was hardcoded to grid-cols-3 at every viewport
  // width, wasting most of a desktop screen. Pagination math (PAGE_SIZE,
  // letter ranges, jump targets) is unchanged; only the rendered column
  // count adapts to the available width.
  const gridRef = useRef<HTMLDivElement>(null);
  const columns = useGridColumns(gridRef, CARD_WIDTH, CARD_GAP, { min: 3 });

  const pageNames = sorted.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);
  const hasPrev   = page > 0;
  const hasNext   = page < totalPages - 1;

  const goNext = () => { if (hasNext) setPage((p) => p + 1); };
  const goPrev = () => { if (hasPrev) setPage((p) => p - 1); };

  const swipe = useSwipePage({ onPrev: goPrev, onNext: goNext });

  const MAX_BTNS = 5;
  const half     = Math.floor(MAX_BTNS / 2);
  const btnStart = Math.max(0, Math.min(page - half, totalPages - MAX_BTNS));
  const btnEnd   = Math.min(totalPages - 1, btnStart + MAX_BTNS - 1);

  return (
    <div className="flex flex-col gap-2" {...swipe}>

      {/* ── Summary label ── */}
      <div className="flex items-center justify-between px-0.5">
        <span className="text-[10px] font-black text-purple-400 uppercase tracking-widest">
          {letterRanges[page] ?? ""}
        </span>
        <span className="text-[10px] text-purple-300">
          {sorted.length} total · {page + 1}/{totalPages}
        </span>
      </div>

      {/* ── Name card grid — column count adapts to container width ── */}
      <div ref={gridRef} className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        <NameCardGrid
          names={pageNames}
          lckRequired={lckRequired}
          compact
          userVotedIds={userVotedIds}
          futuresOddsMap={futuresOddsMap}
          userBalance={userBalance}
          myFuturesBets={myFuturesBets}
          highlightId={highlightId}
        />
      </div>

      {/* ── Nav bar: Prev · letter buttons · Next ── */}
      {totalPages > 1 && (
        <div className="flex items-center gap-1 pt-0.5">
          <NavButton
            label="‹ Prev"
            disabled={!hasPrev}
            align="left"
            onTap={goPrev}
            onDoubleTap={() => setPage(jumpBck[page] ?? 0)}
          />

          <div className="flex-1 flex items-center justify-center gap-1">
            {Array.from({ length: btnEnd - btnStart + 1 }, (_, i) => {
              const btnPage  = btnStart + i;
              const isActive = btnPage === page;
              const isFade   = (btnPage === btnStart && btnStart > 0) ||
                               (btnPage === btnEnd   && btnEnd < totalPages - 1);
              return (
                <button
                  key={btnPage}
                  onClick={() => setPage(btnPage)}
                  className="shrink-0 rounded-lg px-1.5 py-1 text-[9px] font-black transition select-none leading-none"
                  style={{
                    WebkitUserSelect: "none", userSelect: "none",
                    background: isActive ? "#7c3aed" : "#ede9fe",
                    color:      isActive ? "#fff"    : "#7c3aed",
                    opacity:    isFade ? 0.35 : 1,
                    minWidth:   28,
                    transition: "background 0.12s, color 0.12s, opacity 0.12s",
                  }}
                  aria-label={`Page ${btnPage + 1}: ${letterRanges[btnPage]}`}
                >
                  {letterRanges[btnPage]}
                </button>
              );
            })}
          </div>

          <NavButton
            label="Next ›"
            disabled={!hasNext}
            align="right"
            onTap={goNext}
            onDoubleTap={() => setPage(jumpFwd[page] ?? totalPages - 1)}
          />
        </div>
      )}
    </div>
  );
}
